<template>
  <div>
    <div v-if="item.thumbnailPresignedUrl">
      <div class="d-flex mb-5">
        <div
          class="edit-add-on__image-wrapper mr-5 pa-2 rounded-lg"
          :style="{
            backgroundImage: `url('${item.thumbnailPresignedUrl}')`,
            backgroundPosition: 'center',
            backgroundSize: 'cover'
          }"
        ></div>
        <div class="flex-grow-1">
          <div class="d-flex justify-space-between align-center">
            <h4 class="text-h6">{{ item.name }}</h4>
            <CommonButtonIcon
              v-if="!item.required"
              :handleClick="() => (isDeleteModalOpen = true)"
              icon="mdi-trash-can-outline"
            />
          </div>
          <span class="edit-add-on__price">{{ renderAddOnPrice }}</span>
          <p class="mt-3 grey--text">{{ item.description }}</p>
        </div>
      </div>
      <CommonPlayerProfileFormField
        name="quantity"
        :type="RegistrationAnswerType.OPTIONS_CHOOSE_ONE"
        :fieldValue="quantity"
        :items="registrationTrack ? [quantity] : renderQuantity(item)"
        :disabled="registrationTrack"
        :label="$t('player_registration.quantity')"
        :changeFormField="value => handleChangeAddOnItemQuantity(value)"
        :medium="true"
        :rules="{
          required: {
            value: 0
          }
        }"
      />
      <CommonPlayerProfileFormField
        v-for="el in addOnForm"
        :key="el.id"
        :name="el.title"
        :type="el.answerType"
        :fieldValue="el.value"
        :items="renderAddOnSelectOptions(el)"
        :label="el.title"
        :disabled="registrationTrack"
        :changeFormField="value => handleChangeAddOnItemForm(value, el)"
        :rules="renderValidationRules(el)"
      />
    </div>
    <div v-else>
      <v-row class="no-gutters">
        <v-col>
          <span class="font-weight-bold">{{ item.name }}</span>
          <p class="mt-3 grey--text">{{ item.description }}</p>
        </v-col>
        <v-col>
          <span class="edit-add-on__price">{{ renderAddOnPrice }}</span>
        </v-col>
        <v-col cols="2" class="ma-0 pa-0">
          <div class="grey--text">
            Quantity
          </div>
          <div style="height: 20px;">
            <v-select
              class="pa-0 ma-0 quantity"
              :value="quantity"
              :disabled="registrationTrack"
              solo
              dense
              flat
              @change="handleChangeAddOnItemQuantity"
              :items="registrationTrack ? [quantity] : renderQuantity(item)"
            ></v-select>
          </div>
        </v-col>
        <v-col v-if="!item.required" cols="2">
          <CommonButtonIcon
            class="pl-15"
            v-if="!item.required"
            :handleClick="() => (isDeleteModalOpen = true)"
            icon="mdi-trash-can-outline"
          />
        </v-col>
      </v-row>
      <v-row v-for="el in addOnForm" :key="el.id" class="no-gutters">
        <v-col>
          <CommonPlayerProfileFormField
            :name="el.title"
            :type="el.answerType"
            :fieldValue="el.value"
            :items="renderAddOnSelectOptions(el)"
            :label="el.title"
            :changeFormField="value => handleChangeAddOnItemForm(value, el)"
            :rules="renderValidationRules(el)"
          />
        </v-col>
        <v-spacer></v-spacer>
      </v-row>
      <CommonDeleteModal
        :isOpen="isDeleteModalOpen"
        :message="$t('alert.remove_item')"
        :closeModal="() => (isDeleteModalOpen = false)"
        :approveAction="handleRemoveAddOnItem"
      />
    </div>
  </div>
</template>

<script>
import { prepareLocalString } from "../../../helpers/prepareLocalString";
import CommonPlayerProfileFormField from "./CommonPlayerProfileFormField";
import { RegistrationAnswerType } from "../enums/AnswerType";
import CommonButtonIcon from "./CommonButtonIcon";
import CommonDeleteModal from "../common/CommonDeleteModal";
import { mapGetters, mapMutations } from "vuex";
import { ModuleTypes } from "../../../store/ModuleTypes";

export default {
  name: "EditAddOnItem",
  components: {
    CommonButtonIcon,
    CommonPlayerProfileFormField,
    CommonDeleteModal
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    registrationTrack: {
      type: Boolean,
      required: false
    }
  },
  data: () => ({
    quantity: "",
    RegistrationAnswerType,
    isDeleteModalOpen: false
  }),

  created() {
    this.quantity = this.item.quantity;
  },

  computed: {
    ...mapGetters(ModuleTypes.PLAYER_REGISTRATION, [
      "playerRegistrationAddOns",
      "playerRegistrationDetails"
    ]),
    renderAddOnPrice() {
      const priceInCents = this.item.priceIntCents;
      return prepareLocalString(priceInCents / 100);
    },

    addOnForm() {
      return this.item?.addOnOptionList.map(addOn => {
        const playerAnswer = this.item?.registrationPlayerAddOnFormContentList?.find(
          answer => answer?.registrationAddOnFormContentId === addOn?.id
        )?.playerAnswer;
        if (playerAnswer) {
          return {
            ...addOn,
            value: playerAnswer
          };
        } else {
          return addOn;
        }
      });
    }
  },
  methods: {
    ...mapMutations(ModuleTypes.PLAYER_REGISTRATION, [
      "setPlayerRegistrationAddOns"
    ]),
    renderValidationRules(item) {
      return {
        ...(!item.optional
          ? {
              required: {
                value: 0
              }
            }
          : {}),
        ...(item.answerType === RegistrationAnswerType.TEXT
          ? {
              maxLength: {
                value: 255
              }
            }
          : {})
      };
    },

    renderQuantity(item) {
      const {
        customerCanNotPurchaseMoreThan,
        customerMustPurchaseAtLeast
      } = item;

      const usedQuantity =
        this.playerRegistrationAddOns
          .filter(
            ({ addOnId, isEditMode }) =>
              addOnId === this.item.addOnId && isEditMode
          )
          .map(({ quantity }) => quantity)
          .reduce((acc, item) => acc + item) - this.item.quantity;
      const extraIteration = 1;
      let quantity;
      if (
        customerCanNotPurchaseMoreThan !== null &&
        customerCanNotPurchaseMoreThan <= 100
      ) {
        quantity =
          customerCanNotPurchaseMoreThan -
          usedQuantity -
          customerMustPurchaseAtLeast +
          extraIteration;
      } else {
        quantity =
          100 - customerMustPurchaseAtLeast - usedQuantity + extraIteration;
      }

      const quantityOptionsList = Array.from(
        { length: quantity },
        (v, k) => k + customerMustPurchaseAtLeast
      );

      if (customerMustPurchaseAtLeast === 0) {
        quantityOptionsList.shift();
      }
      return quantityOptionsList;
    },

    renderAddOnSelectOptions(item) {
      return item?.answers?.map(({ answerTitle, priceInCents: price }) => {
        const priceInCents = price;
        return {
          value: answerTitle,
          name: `${answerTitle}${
            price ? ` - ${prepareLocalString(priceInCents / 100)} ` : ""
          }`
        };
      });
    },

    handleRemoveAddOnItem() {
      const filteredAddOns = [...this.playerRegistrationAddOns].filter(
        addOn => addOn.id !== this.item.id
      );
      this.setPlayerRegistrationAddOns({
        playerRegistrationAddOns: filteredAddOns
      });
    },

    handleChangeAddOnItemForm(value, addOnOption) {
      this.setPlayerRegistrationAddOns({
        playerRegistrationAddOns: [...this.playerRegistrationAddOns].map(
          item => {
            if (item.id === this.item.id) {
              const registrationPlayerAddOnFormContentList =
                item?.registrationPlayerAddOnFormContentList || [];
              const existingOption = registrationPlayerAddOnFormContentList?.find(
                item => item.registrationAddOnFormContentId === addOnOption.id
              );
              if (existingOption) {
                const index = registrationPlayerAddOnFormContentList.findIndex(
                  el => el.registrationAddOnFormContentId === addOnOption.id
                );

                if (index !== -1) {
                  registrationPlayerAddOnFormContentList.splice(index, 1, {
                    registrationAddOnFormContentId: addOnOption.id,
                    playerAnswer: value
                  });
                }
              } else {
                registrationPlayerAddOnFormContentList.push({
                  registrationAddOnFormContentId: addOnOption.id,
                  playerAnswer: value
                });
              }
              return {
                ...item,
                registrationPlayerAddOnFormContentList
              };
            }
            return item;
          }
        )
      });
    },

    handleChangeAddOnItemQuantity(value) {
      this.setPlayerRegistrationAddOns({
        playerRegistrationAddOns: [...this.playerRegistrationAddOns].map(
          item => {
            if (item.id === this.item.id) {
              return {
                ...this.item,
                quantity: value
              };
            }
            return item;
          }
        )
      });
    }
  },
  mounted() {
    if (this.item.customerMustPurchaseAtLeast !== 0) {
      this.quantity = this.item.customerMustPurchaseAtLeast;
      this.handleChangeAddOnItemQuantity(this.quantity);
    }
    if (this.registrationTrack) {
      this.quantity = this.item.quantity;
      this.item.addOnOptionList.map(i => (i.value = i.answer));
    }
  }
};
</script>

<style scoped>
.edit-add-on__image-wrapper {
  min-width: 120px;
  min-height: 120px;
  border: 1px solid var(--divider-color);
}

.edit-add-on__price {
  font-size: 15px;
  font-weight: var(--font-weight-medium);
}
.quantity {
  padding: 0;
  margin: 0;
}
</style>
